'use client';
import React, { useState } from 'react';
import { Search, Plus, Edit2, Trash2, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const mockMiracles = [
  { id: 'miracle-001', category: 'finances', title: 'The River of Prosperity', preview: 'Abundance is not a destination — it is a frequency you are learning to hold...', timesDelivered: 12483, active: true, createdAt: 'Jan 12, 2026', author: 'Elder Miriam' },
  { id: 'miracle-002', category: 'love', title: 'The Heart That Was Always Yours', preview: 'The love you seek has already written your name in its heart...', timesDelivered: 10284, active: true, createdAt: 'Jan 12, 2026', author: 'Elder Miriam' },
  { id: 'miracle-003', category: 'health', title: 'Sacred Vessel of Light', preview: 'Your body is a sacred vessel of light. The healing that you have prayed for...', timesDelivered: 9847, active: true, createdAt: 'Jan 15, 2026', author: 'Sage Kofi' },
  { id: 'miracle-004', category: 'peace', title: 'The Stillness Beneath the Storm', preview: 'The storm inside you is not who you are — it is simply passing through...', timesDelivered: 8432, active: true, createdAt: 'Feb 01, 2026', author: 'Sage Kofi' },
  { id: 'miracle-005', category: 'career', title: 'You Were Not Placed Here to Go Unnoticed', preview: 'The gifts you have been quietly building are about to be seen...', timesDelivered: 7819, active: true, createdAt: 'Feb 14, 2026', author: 'Seer Nadia' },
  { id: 'miracle-006', category: 'family', title: 'The Bonds That Cannot Break', preview: 'The bonds of your family are stronger than any distance or disagreement...', timesDelivered: 7204, active: true, createdAt: 'Feb 28, 2026', author: 'Elder Miriam' },
  { id: 'miracle-007', category: 'finances', title: 'The Door That Opens Inward', preview: 'A door is about to open that you did not expect. Walk through it...', timesDelivered: 6891, active: true, createdAt: 'Mar 10, 2026', author: 'Seer Nadia' },
  { id: 'miracle-008', category: 'health', title: 'Quiet Restoration', preview: 'Release the grip of fear — wellness is not coming, it is arriving...', timesDelivered: 5230, active: false, createdAt: 'Mar 22, 2026', author: 'Sage Kofi' },
  { id: 'miracle-009', category: 'love', title: 'Open Your Hands', preview: 'Let go of what was, and watch as something far more beautiful...', timesDelivered: 4847, active: true, createdAt: 'Apr 01, 2026', author: 'Elder Miriam' },
  { id: 'miracle-010', category: 'peace', title: 'Return to Breath', preview: 'The clarity you are seeking is not far away — it is what remains...', timesDelivered: 4123, active: true, createdAt: 'Apr 15, 2026', author: 'Seer Nadia' },
];

const categoryColors: Record<string, string> = {
  finances: 'text-primary bg-primary/10',
  love: 'text-pink-400 bg-pink-400/10',
  health: 'text-success bg-success/10',
  peace: 'text-fuchsia-300 bg-fuchsia-300/10',
  career: 'text-violet-400 bg-violet-400/10',
  family: 'text-blue-400 bg-blue-400/10',
};

export default function AdminMiraclesTable() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 6;

  const filtered = mockMiracles.filter(
    (m) =>
      m.title.toLowerCase().includes(search.toLowerCase()) ||
      m.category.toLowerCase().includes(search.toLowerCase()) ||
      m.author.toLowerCase().includes(search.toLowerCase())
  );
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  return (
    <div className="cosmic-card rounded-2xl border border-border overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <h3 className="font-display font-semibold text-foreground">Miracle Message Library</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{mockMiracles.length} messages · {mockMiracles.filter(m => m.active).length} active</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-56">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search messages…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50"
            />
          </div>
          <button
            onClick={() => toast.success('New miracle message editor — coming soon')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-mediumtransition-all active:scale-95"
            style={{ background: 'linear-gradient(135deg, #a07830, #c9a84c)', color: '#0a0a14' }}
          >
            <Plus size={15} />
            Add Miracle
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {['Title', 'Category', 'Preview', 'Author', 'Times Delivered', 'Created', 'Status', 'Actions'].map((col) => (
                <th key={`mcol-${col}`} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((miracle, i) => (
              <tr key={miracle.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors group"
                style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                <td className="px-4 py-3 font-display text-xs font-semibold text-foreground whitespace-nowrap max-w-[180px] truncate">
                  {miracle.title}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${categoryColors[miracle.category] || 'text-muted-foreground bg-muted'}`}>
                    {miracle.category}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs max-w-[240px] truncate">{miracle.preview}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{miracle.author}</td>
                <td className="px-4 py-3 text-foreground font-semibold metric-number text-center">{miracle.timesDelivered.toLocaleString()}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{miracle.createdAt}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    miracle.active ? 'text-success bg-success/10' : 'text-muted-foreground bg-muted'
                  }`}>
                    {miracle.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button title="Preview miracle message" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
                      <Eye size={14} />
                    </button>
                    <button
                      title="Edit miracle message"
                      onClick={() => toast.success(`Editing: ${miracle.title}`)}
                      className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      title="Delete miracle message — this cannot be undone"
                      onClick={() => toast.error('Deleting a miracle message requires confirmation')}
                      className="p-1.5 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-5 py-3 border-t border-border">
        <p className="text-xs text-muted-foreground">Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length} messages</p>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground disabled:opacity-40 transition-all">
            <ChevronLeft size={15} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button key={`mir-page-${p}`} onClick={() => setPage(p)}
              className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${page === p ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted'}`}>
              {p}
            </button>
          ))}
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground disabled:opacity-40 transition-all">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}